import { changeI18nLanguage, translate } from './i18n.js';
import {
  DEFAULT_LANGUAGE,
  filterSupportedLanguages,
  getLocale,
  normalizeLanguageCode,
  syncDocumentLocale,
} from './localeManifest.js';

export const LANGUAGE_STORAGE_KEY = 'atrux.language';

let state = {
  activeLanguage: DEFAULT_LANGUAGE,
  defaultLanguage: DEFAULT_LANGUAGE,
  languages: [],
};
// A language explicitly selected in this browser session is authoritative over
// profile GETs that may have started before the preference PATCH completed.
// It is reset when a new authenticated user is established.
let userLanguageSelection = null;
const listeners = new Set();

const emit = (change) => listeners.forEach((listener) => listener(state, change));

const createLanguageChange = (source, previousLanguage, nextLanguage) => ({
  source,
  previousLanguage,
  nextLanguage,
});

const syncLanguageEnvironment = (language) => {
  syncDocumentLocale(language);
  // Store mutations are intentionally synchronous. Handle the asynchronous
  // i18next transition explicitly so a future lazy bundle failure cannot become
  // an unhandled rejection. Interactive changes await the same ordered helper
  // in LanguageProvider and can still perform a full rollback there.
  void changeI18nLanguage(language).catch((error) => {
    console.error(`Failed to activate i18n language "${language}":`, error);
  });
};

export const languageStore = {
  getState: () => state,
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  configure(registry, user = null, locallySaved = null) {
    const advertisedLanguages = filterSupportedLanguages(registry?.languages);
    if (advertisedLanguages.length === 0) {
      throw new Error('The backend language registry has no frontend-supported languages.');
    }
    userLanguageSelection = null;
    const languages = advertisedLanguages;
    const supported = new Set(languages.map((item) => item.code));
    const requestedDefault = normalizeLanguageCode(registry?.default);
    const defaultLanguage = requestedDefault && supported.has(requestedDefault)
      ? requestedDefault
      : supported.has(DEFAULT_LANGUAGE)
        ? DEFAULT_LANGUAGE
        : languages[0].code;
    const userLanguage = normalizeLanguageCode(user?.preferred_language);
    const savedLanguage = normalizeLanguageCode(locallySaved);
    const activeLanguage = supported.has(userLanguage)
      ? userLanguage
      : supported.has(savedLanguage)
        ? savedLanguage
        : defaultLanguage;
    const previousLanguage = state.activeLanguage;
    state = { activeLanguage, defaultLanguage, languages };
    syncLanguageEnvironment(activeLanguage);
    emit(createLanguageChange('configure', previousLanguage, activeLanguage));
    return activeLanguage;
  },
  primeActiveLanguage(language) {
    const normalizedLanguage = normalizeLanguageCode(language);
    if (!normalizedLanguage) return state.activeLanguage;

    // This runs before the remote language registry is available. A locally
    // cached, manifest-supported preference is safe for the bootstrap screen;
    // `configure` will reconcile it with the authoritative registry later.
    const previousLanguage = state.activeLanguage;
    state = { ...state, activeLanguage: normalizedLanguage };
    syncLanguageEnvironment(normalizedLanguage);
    emit(createLanguageChange('bootstrap', previousLanguage, normalizedLanguage));
    return normalizedLanguage;
  },
  setActiveLanguage(language, { source = 'direct' } = {}) {
    const normalizedLanguage = normalizeLanguageCode(language);
    if (!normalizedLanguage) return state.activeLanguage;
    if (state.languages.length > 0 && !state.languages.some((item) => item.code === normalizedLanguage)) {
      return state.activeLanguage;
    }
    const previousLanguage = state.activeLanguage;
    state = { ...state, activeLanguage: normalizedLanguage };
    syncLanguageEnvironment(normalizedLanguage);
    emit(createLanguageChange(source, previousLanguage, normalizedLanguage));
    return normalizedLanguage;
  },
  selectUserLanguage(language) {
    const normalizedLanguage = normalizeLanguageCode(language);
    if (!normalizedLanguage) return state.activeLanguage;
    if (state.languages.length > 0 && !state.languages.some((item) => item.code === normalizedLanguage)) {
      return state.activeLanguage;
    }
    userLanguageSelection = normalizedLanguage;
    return this.setActiveLanguage(normalizedLanguage, { source: 'user-selection' });
  },
  resetUserLanguageSelection() {
    userLanguageSelection = null;
  },
  reconcileUser(user) {
    if (!user || typeof user !== 'object' || !userLanguageSelection) return user;
    if (normalizeLanguageCode(user.preferred_language) === userLanguageSelection) return user;
    return { ...user, preferred_language: userLanguageSelection };
  },
  applyUser(user) {
    const synchronizedUser = this.reconcileUser(user);
    const language = normalizeLanguageCode(synchronizedUser?.preferred_language);
    if (language && state.languages.some((item) => item.code === language)) {
      this.setActiveLanguage(language, { source: 'apply-user' });
    }
    return synchronizedUser;
  },
};

export const getActiveLanguage = () => languageStore.getState().activeLanguage;
export const getActiveLocale = () => getLocale(getActiveLanguage());
export const t = (key, params) => translate(getActiveLanguage(), key, params);
